import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { BrowserRouter, Switch, Route } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';

import 'config/sentry';
import { MixpanelContext, mixpanelLibrary } from 'config/analytics';
import { theme } from 'config/theme';
import './fonts/index.css';

import I18nProvider from 'providers/I18nProvider';

import HomeScreen from './screens/HomeScreen';
import TrackingScreen from './screens/TrackingScreen';
import CourierSurveyScreen from './screens/CourierSurveyScreen';
import UnsubscribeScreen from './screens/UnsubscribeScreen';
import RedirectScreen from './screens/RedirectScreen';

import RecaptchaService from 'services/recaptcha';

function DefaultHelmet() {
  const { formatMessage } = useIntl();
  const title = formatMessage({ id: 'meta.app-title' });
  const description = formatMessage({ id: 'meta.app-description' });

  return (
    <Helmet>
      <meta charSet="utf-8" />
      <title>{title}</title>
      <meta name="description" content={description} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://www.trackmyshipment.co/" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content="https://www.trackmyshipment.co/images/1-ready-to-ship.svg" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content="https://www.trackmyshipment.co/" />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content="https://www.trackmyshipment.co/images/1-ready-to-ship.svg" />
    </Helmet>
  );
}

function App() {
  const [isInitializingToken, setIsInitializingToken] = useState(true);

  useEffect(() => {
    RecaptchaService.init(true).then(() => setIsInitializingToken(false));
  }, []);

  return (
    <HelmetProvider>
      {!isInitializingToken && (
        <MixpanelContext.Provider value={mixpanelLibrary}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <I18nProvider>
              <DefaultHelmet />
              <BrowserRouter>
                <Switch>
                  <Route path="/shipment-tracking/:keyword/:companyId?">
                    <TrackingScreen />
                  </Route>
                  <Route path="/track/:keyword">
                    <TrackingScreen />
                  </Route>
                  <Route path="/surveys/:userType/:surveyId">
                    <CourierSurveyScreen />
                  </Route>
                  <Route path="/unsubscribe">
                    <UnsubscribeScreen />
                  </Route>
                  <Route path="/shipment-redirect/:action">
                    <RedirectScreen />
                  </Route>
                  <Route path="/">
                    <HomeScreen />
                  </Route>
                </Switch>
              </BrowserRouter>
            </I18nProvider>
          </ThemeProvider>
        </MixpanelContext.Provider>
      )}
    </HelmetProvider>
  );
}

export default App;
